/**
 * client/src/lib/use-credential-added-push.ts
 *
 * #3169 — client consumer of the owner-addressed `security:credential_added`
 * WS frame (server: `server/logic/security-tier-ladder/
 * credential-append-notice.ts`, `notifyCredentialAppended`'s `broadcastToUser`
 * call). Found during the Stage 7 module 01 Task 3 review (passkey boundary
 * gaps, AC-1): the server has broadcast this frame since Task 2, but nothing
 * under `client/src/` listened for it, so a live "your other device just
 * gained a passkey" notice reached no one until the durable feed row
 * (module 01 Task 2/3) refreshed on its own.
 *
 * Mirrors `use-removal-hold-push.ts` exactly (Reuse-First Check,
 * DEVELOPMENT/quality.md Code Review Criteria #8): same `useCurrentUser()`
 * readiness gate, same `{ type:"auth", userId, sessionId }` handshake shape,
 * same `resetKey`.
 *
 * ONLY the owner-addressed frame is consumed here. The sibling
 * `guardian:credential_added` frame is out of scope (issue body, Refs
 * #3165) — it carries no `href` by design (Rule 10: the guardian frame never
 * names which credential), so there is nothing for a guardian consumer to
 * link to; a guardian's own durable read stays the feed.
 *
 * REACH IS NOT GUARANTEED — a WebSocket notice reaches a LIVE session only.
 * The durable feed row is the offline audience's signal (module 01 Task 2/3);
 * this hook is defence in depth over it, same honest ceiling as every other
 * push listener in this file's family.
 */

import { useCurrentUser } from "@/lib/use-current-user";
import { useReconnectingWs, type UseReconnectingWsResult } from "@/lib/use-reconnecting-ws";

/** Pinned against the server's own literal — see the cross-file lock test in
 *  `tests/unit/use-credential-added-push.test.ts`. */
export const CREDENTIAL_ADDED_EVENT = "security:credential_added" as const;

export interface CredentialAddedFrame extends Record<string, unknown> {
  type: typeof CREDENTIAL_ADDED_EVENT;
  credentialRecordId?: string;
  credentialId?: string;
  href?: string;
  timestamp?: string;
  message?: string;
}

/**
 * Listens for the owner-addressed credential-added push. A null/loading
 * current user opens no socket at all (readiness-gate contract, same as
 * `useRemovalHoldPush`).
 */
export function useCredentialAddedPush(
  onEvent: (frame: CredentialAddedFrame) => void,
): UseReconnectingWsResult {
  const { user } = useCurrentUser();
  return useReconnectingWs<CredentialAddedFrame>(
    () =>
      user?.id
        ? { type: "auth", userId: user.id, sessionId: `credential-added-push-${user.id}` }
        : null,
    (msg) => msg.type === CREDENTIAL_ADDED_EVENT,
    onEvent,
    user?.id,
  );
}
